import { report } from '../report';
import Signal from './Signal';
import EventEmitter from 'eventemitter3';

export function sleep(durationInMs: number) {
    // Check for Infinity
    if (durationInMs > 0 && !isFinite(durationInMs)) {
        return createNeverResolvingPromise<void>();
    }

    return new Promise<void>(resolve => {
        setTimeout(() => {
            resolve();
        }, durationInMs);
    });
}

/**
 * Returns `true` if the given promise resolves successfully before the specified timeout duration. Returns `false` if
 * the timeout expires or the promise rejects or throws an error.
 * @param durationInMs
 * @param promise Promise to race against the specified timeout duration.
 */
export async function timeoutPromise<T>(durationInMs: number, promise: Promise<T>): Promise<boolean> {
    return Promise.race([didResolve(promise), didResolve(sleep(durationInMs))]);
}

/**
 * Convert a Promise<T> to Promise<boolean> to return `true` if it resolves successfully or `false` if it rejects for whatever reason.
 * @param promise
 */
export async function didResolve<T>(promise: Promise<T>): Promise<boolean> {
    try {
        await promise;
    } catch (e) {
        return false;
    }

    return true;
}

export function getResolvedPromise(): Promise<void> {
    return new Promise(res => res());
}

function createNeverResolvingPromise<T = unknown>() {
    return new Promise(() => {}) as Promise<T>;
}

export function createThrottler(intervalMs: number) {
    let lastResolutionTimestamp = 0;
    return throttle;
    async function throttle() {
        const now = Date.now();
        const resolveAt = lastResolutionTimestamp + intervalMs;
        lastResolutionTimestamp = Math.max(resolveAt, now);
        if (resolveAt > now) await sleep(resolveAt - now);
    }
}

/**
 * A simple version of throttle using setTimeout
 * Ensure that a callback is called only once in that limit of time
 */

export function throttle(callback: () => void, limit: number) {
    var waiting = false;
    return function() {
        if (!waiting) {
            callback.apply(this, arguments);
            waiting = true;
            setTimeout(function() {
                waiting = false;
            }, limit);
        }
    };
}

/**
 * An asynchronous version of throttle using setTimeout
 * Ensure that a callback is called only once in that limit of time
 */

export function throttleAsync(callback: (...params: any[]) => Promise<void>, limit?: number) {
    var waiting = false;
    return async function(...params: any[]) {
        if (!waiting) {
            waiting = true;
            if (limit) {
                setTimeout(function() {
                    waiting = false;
                }, limit);
            }
            await callback.apply(this, params);

            waiting = false;
        }
    };
}

/**
 * This helper function does two things:
 * 1. Wraps given action to avoid running it in parallel to itself
 * 2. Returns result to every place that attempted to run the action, preserving order of execution
 * It does not cache result, so after the action is over it is possible to start it again
 */
export function shareAsyncAction<T = void>(action: () => Promise<T>) {
    let current: null | Promise<T> = null;
    return getCurrentOrExecute;
    async function getCurrentOrExecute() {
        if (current === null) {
            // Chaining to Promise.resolve() allows to prevent `action` from starting synchronously,
            // so that even if it goes recursive the `current` is already assigned
            current = Promise.resolve()
                .then(action)
                .then(
                    result => {
                        current = null;
                        return result;
                    },
                    error => {
                        current = null;
                        throw error;
                    }
                );
        }
        return current;
    }
}

/**
 * Wraps the given function so that subsequent calls reuse result of the first execution
 * Note: It does not automatically call the given action straight away.
 */
export function cached<T>(action: () => T) {
    let started = false;
    let finished = false;
    let result: T | undefined;
    return () => {
        if (!finished) {
            // Shouldn't happen
            if (started) {
                report('Cached action is already executing', { once: true });
            }
            started = true;
            result = action();
            finished = true;
        }
        return result as T;
    };
}

// Retry

export interface GetRetryDelayMs {
    /** return `undefined` to give up and propagate up last error */
    (retryIndex: number): number | undefined;
}

const defaultGetRetryDelayMs: GetRetryDelayMs = index => [100, 500][index];

interface RetryResult<T> {
    promise: Promise<T>;
    cancel: (waitForRetryDelay?: boolean) => Promise<void>;
}

/**
 * WARNING: it's easy to forget and do `await retry(...)` instead of `await retry(...).promise`
 * Keep an eye for that
 */
export function retry<T = unknown>(
    /** action to retry, can be asynchronous */
    action: () => T | Promise<T>,
    params: {
        getRetryDelayMs?: GetRetryDelayMs;
        onError?: (error: unknown, attemptIndex: number) => void;
    } = {}
): RetryResult<T> {
    // Default params
    const { getRetryDelayMs = defaultGetRetryDelayMs, onError } = params;

    /**
     * Place to keep the promise of currently executed `action`,
     * so that `cancel` can report back when it's fully done
     * (e.g. to know when it's safe to start anew)
     */
    let currentActionPromise: Promise<unknown> = Promise.resolve();
    let currentCooldownPromise: Promise<void> = Promise.resolve();
    let cancelled = false;

    return {
        promise: start(),
        cancel,
    };

    async function start(): Promise<T> {
        let attemptIndex = 0;
        do {
            try {
                // Wrap in promise it case it isn't async
                currentActionPromise = Promise.resolve().then(action);
                const result = (await currentActionPromise) as T;

                // Let garbage collector do its job
                currentActionPromise = Promise.resolve();

                // Check cancel
                if (cancelled) return createNeverResolvingPromise<T>();

                return result;
            } catch (error) {
                if (!cancelled && onError) onError(error, attemptIndex);

                const delayToNextRetry = getRetryDelayMs(attemptIndex++);

                // Special case indicating that it's time to give up
                if (delayToNextRetry === undefined) {
                    // Check cancel
                    if (cancelled) return createNeverResolvingPromise<T>();

                    throw error;
                }

                currentCooldownPromise = sleep(delayToNextRetry);
                await currentCooldownPromise;

                // Check cancel
                if (cancelled) return createNeverResolvingPromise<T>();
            }
        } while (true);
    }

    function cancel(waitForRetryDelay: boolean = false) {
        cancelled = true;
        let cancelFinishedPromise: Promise<void> = currentActionPromise.then(
            // Hide result (one shouldn't get it from calling `cancel`)
            () => {},
            // Ignore errors, all there is to know is when the action has settled
            () => {}
        );
        if (waitForRetryDelay) {
            cancelFinishedPromise = cancelFinishedPromise.then(() => currentCooldownPromise);
        }
        return cancelFinishedPromise;
    }
}

export class Retrier<T = unknown> {
    private consecutiveFailsCount = 0;

    private actionToRunNext: null | (() => Promise<T>) = null;

    private current: null | RetryResult<T> = null;
    private cancellingCurrent = false;

    private readonly getRetryDelayMs: GetRetryDelayMs = defaultGetRetryDelayMs;
    private readonly onError?: (error: unknown, attemptIndex: number) => void;

    constructor(
        getRetryDelayMs: GetRetryDelayMs = defaultGetRetryDelayMs,
        onError?: (error: unknown, attemptIndex: number) => void
    ) {
        this.getRetryDelayMs = getRetryDelayMs;
        this.onError = onError;
    }

    public async restart(action: () => Promise<T>, resetFailsCount: boolean) {
        this.actionToRunNext = action;
        if (resetFailsCount) {
            this.consecutiveFailsCount = 0;
        }
        if (this.cancellingCurrent) return;
        if (this.current) {
            this.cancellingCurrent = true;
            await this.current.cancel(!resetFailsCount);
            this.cancellingCurrent = false;
        }
        // not using `action` since it might've change while waiting for cancel
        const actionToRunNext = this.actionToRunNext;
        this.actionToRunNext = null;
        this.current = retry(
            async () => {
                const result = await actionToRunNext();
                this.consecutiveFailsCount = 0;
                return result;
            },
            {
                getRetryDelayMs: () => this.getRetryDelayMs.call(null, this.consecutiveFailsCount),
                onError: (error, attemptIndex) => {
                    if (this.onError) this.onError.call(null, error, this.consecutiveFailsCount);
                    // FIXME: this doesn't play nice with `resetFailsCount`
                    this.consecutiveFailsCount += 1;
                },
            }
        );
        return this.current.promise;
    }
}

//

class CancelifiedPromise<T> implements Promise<T> {
    private readonly isCancelled: () => boolean;
    private readonly promise: Promise<T>;
    [Symbol.toStringTag]: string;

    constructor(isCancelled: () => boolean, promise: Promise<T>) {
        this.isCancelled = isCancelled;
        this.promise = promise;
        this[Symbol.toStringTag] = this.promise[Symbol.toStringTag];
    }

    // @ts-ignore TS doesn't understand that if onfulfilled is not provided, result can be T
    public then<TResult1 = T, TResult2 = never>(
        onfulfilled?: ((result: T) => TResult1 | PromiseLike<TResult1>) | null,
        onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
    ): Promise<TResult1 | TResult2 | T> {
        if (this.isCancelled.call(null)) {
            return createNeverResolvingPromise<TResult1>();
        }

        return this.promise.then(
            // @ts-ignore TS doesn't understand that if onfulfilled is not provided, result can be T
            result => {
                if (this.isCancelled.call(null)) {
                    return createNeverResolvingPromise<TResult1>();
                }

                if (typeof onfulfilled === 'function') {
                    return onfulfilled(result);
                }
                return result;
            },
            error => {
                if (this.isCancelled.call(null)) {
                    return createNeverResolvingPromise<TResult2>();
                }

                if (typeof onrejected === 'function') {
                    return onrejected(error);
                }
                throw error;
            }
        );
    }

    public catch<TResult = never>(onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | null) {
        if (this.isCancelled.call(null)) {
            return createNeverResolvingPromise<TResult>();
        }

        return this.promise.catch(error => {
            if (this.isCancelled.call(null)) {
                return createNeverResolvingPromise<TResult>();
            }

            if (typeof onrejected === 'function') {
                return onrejected(error);
            }
            throw error;
        });
    }

    public finally(...args: Parameters<Promise<T>['finally']>) {
        const [onsettled] = args;
        if (this.isCancelled.call(null)) {
            return createNeverResolvingPromise<T>();
        }

        return this.promise.finally(async () => {
            if (this.isCancelled.call(null)) {
                return createNeverResolvingPromise<T>();
            }

            if (typeof onsettled === 'function') {
                return onsettled();
            }
            return;
        });
    }
}

export function cancelifyPromise<T>(isCancelled: () => boolean, promise: Promise<T>): Promise<T> {
    if (isCancelled()) return createNeverResolvingPromise();
    return new CancelifiedPromise(isCancelled, promise) as Promise<T>;
}

export function promiseAllSettled<T>(...promises: Promise<T>[]): Promise<T[]> {
    const caughtPromises = promises.map(promise => promise.catch(error => error));
    return Promise.all(caughtPromises);
}

export function promisifySignal<T extends unknown[]>(signal: Signal<T>): Promise<T> {
    return new Promise(resolve => {
        signal.addListenerOnce((...args) => {
            // @ts-ignore
            resolve(...args);
        });
    });
}

export function promisifyEvent(eventEmitter: GameObject | EventEmitter, eventName: string): Promise<void> {
    return new Promise(resolve => {
        eventEmitter.once(eventName, resolve);
    });
}

export function* generifySignal(signal: Signal<any>): IterableIterator<any> {
    let wait = true;

    signal.addListenerOnce(() => {
        wait = false;
    });

    while (wait) yield;
}

export function* generifyPromise(promise: Promise<any>): IterableIterator<any> {
    let wait = true;

    promise.then(() => {
        wait = false;
    });

    while (wait) yield;
}

// Creates a debounced function that delays invoking func until after wait milliseconds have elapsed since the last time the debounced function was invoked
// Similar to lodash debounce but without cancel and flush methods on the created function
export function debounce(func: (...args: unknown[]) => void, wait: number, immediate: boolean = false) {
    let timeout: number | null;

    return function executedFunction(...args: any[]) {
        const context = this;

        const later = () => {
            timeout = null;

            if (!immediate) {
                func.call(context, ...args);
            }
        };

        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = window.setTimeout(later, wait);

        if (callNow) {
            func.call(context, ...args);
        }
    };
}
